import { useEffect, useRef } from 'react';
import { Command } from 'cmdk';
import './CommandPalette.css';

function flattenMenu(menuConfig, userRoles = []) {
    const hasAccess = (roles) => !roles || roles.some((r) => userRoles.includes(r));
    const items = [];

    for (const item of menuConfig) {
        if (!hasAccess(item.roles)) continue;

        if (item.children) {
            for (const child of item.children) {
                if (!hasAccess(child.roles)) continue;
                items.push({
                    id: child.href,
                    label: child.label,
                    parent: item.label,
                    icon: item.icon,
                    href: child.href,
                    keywords: [...(item.keywords ?? []), ...(child.keywords ?? [])],
                    description: child.description ?? item.description ?? '',
                });
            }
        } else {
            items.push({
                id: item.to,
                label: item.label,
                parent: null,
                icon: item.icon,
                href: item.to,
                keywords: item.keywords ?? [],
                description: item.description ?? '',
            });
        }
    }

    return items;
}

export default function CommandPalette({ open, setOpen, menuConfig, user }) {
    const inputRef = useRef(null);
    const paletteRef = useRef(null);

    const items = flattenMenu(menuConfig, user?.roles);

    // Focus automatique à l'ouverture
    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [open]);

    // Fermeture sur Escape
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') setOpen(false);
    };

    const handleSelect = (href) => {
        setOpen(false);
        window.location.href = href;
    };

    const disableHover = () => {
        paletteRef.current?.setAttribute('data-keyboard', 'true');
    };

    const enableHover = () => {
        paletteRef.current?.removeAttribute('data-keyboard');
    };

    if (!open) return null;

    return (
        <div className="command-palette-overlay" onClick={() => setOpen(false)}>
            <div
                ref={paletteRef}
                className="command-palette-wrapper"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    handleKeyDown(e);
                    disableHover();
                }}
                onMouseMove={enableHover}
            >
                <Command className="command-palette" label="Navigation">
                    <div className="command-palette-input-wrapper">
                        <svg className="command-palette-search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <Command.Input ref={inputRef} className="command-palette-input" placeholder="Rechercher une page..." />
                        <kbd className="command-palette-esc">Esc</kbd>
                    </div>

                    <Command.List className="command-palette-list">
                        <Command.Empty className="command-palette-empty">Aucun résultat trouvé.</Command.Empty>

                        {items.map((item) => (
                            <Command.Item key={item.id} value={`${item.label} ${item.parent ?? ''} ${item.keywords.join(' ')}`} className="command-palette-item" onSelect={() => handleSelect(item.href)}>
                                <span className="command-palette-item-icon">{item.icon}</span>
                                <span className="command-palette-item-text">
                                    <span className="command-palette-item-label">{item.label}</span>
                                    {item.parent && <span className="command-palette-item-parent">{item.parent}</span>}
                                    {item.description && <span className="command-palette-item-description">{item.description}</span>}
                                </span>
                                <svg className="command-palette-item-arrow" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </Command.Item>
                        ))}
                    </Command.List>

                    <div className="command-palette-footer">
                        <span>
                            <kbd>↑</kbd>
                            <kbd>↓</kbd> Naviguer
                        </span>
                        <span>
                            <kbd>↵</kbd> Ouvrir
                        </span>
                        <span>
                            <kbd>Ctrl K</kbd> Fermer
                        </span>
                    </div>
                </Command>
            </div>
        </div>
    );
}
